import React from 'react';
import { 
  Brain, 
  Clock, 
  Repeat, 
  Eye, 
  Frown,
  Zap,
  ClipboardList,
  Wind,
  ShieldCheck
} from 'lucide-react';

const FamilySystemComparison = () => {
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-muted/30 via-background to-secondary/10 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              What Changes When You Have a Family System
            </h2>
          </div>

          {/* Side-by-Side Container */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* LEFT SIDE: THE MENTAL LOAD */}
            <div className="relative">
              {/* Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-muted/60 rounded-xl flex items-center justify-center">
                  <Brain className="w-6 h-6 text-muted-foreground/60" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground/70 uppercase tracking-wider">Before the system</p>
                  <h3 className="text-2xl font-bold text-muted-foreground">The Mental Load</h3>
                </div>
              </div>

              {/* Cluttered "Mental Load" Bubbles - Desktop scattered, Mobile stacked */}
              <div className="flex flex-col min-h-[520px] md:min-h-[580px]">
                
                {/* Bubbles Container - takes available space */}
                <div className="relative flex-1">
                  {/* Bubble 1: Blurred Plans */}
                  <div className="md:absolute md:top-0 md:left-0 md:right-8 md:-rotate-1 mb-4 md:mb-0">
                    <div className="bg-muted/40 border border-border/50 rounded-xl p-5 shadow-sm">
                      <div className="flex items-start gap-3">
                        <Brain className="w-5 h-5 text-muted-foreground/50 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-muted-foreground font-medium italic">
                            "Did I say that, or did I only think it?"
                          </p>
                          <p className="text-muted-foreground/60 text-sm mt-1">
                            Conversations blur. You can't remember if a plan was confirmed or just discussed in passing.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bubble 2: The Tracking Burden */}
                  <div className="md:absolute md:top-[100px] md:left-4 md:right-0 md:rotate-1 mb-4 md:mb-0">
                    <div className="bg-muted/30 border border-border/40 rounded-xl p-5 shadow-sm">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-muted-foreground/50 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-muted-foreground font-medium italic">
                            "If I don't track it, it's gone."
                          </p>
                          <p className="text-muted-foreground/60 text-sm mt-1">
                            The burden of remembering appointments, meds, and logistics falls entirely on your memory.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bubble 3: The Constant Relay */}
                  <div className="md:absolute md:top-[200px] md:left-0 md:right-6 md:-rotate-2 mb-4 md:mb-0">
                    <div className="bg-muted/35 border border-border/45 rounded-xl p-5 shadow-sm">
                      <div className="flex items-start gap-3">
                        <Repeat className="w-5 h-5 text-muted-foreground/50 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-muted-foreground/80 font-semibold text-sm uppercase tracking-wide">
                            The Constant Relay
                          </p>
                          <p className="text-muted-foreground/60 text-sm mt-1">
                            You spend half your day repeating the same update to three different people.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bubble 4: Mentally Always On */}
                  <div className="md:absolute md:top-[300px] md:left-6 md:right-0 md:rotate-1 mb-4 md:mb-0">
                    <div className="bg-muted/25 border border-border/35 rounded-xl p-5 shadow-sm">
                      <div className="flex items-start gap-3">
                        <Eye className="w-5 h-5 text-muted-foreground/50 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-muted-foreground font-medium italic">
                            "Physically here, mentally auditing."
                          </p>
                          <p className="text-muted-foreground/60 text-sm mt-1">
                            You're physically present, but mentally you're always checking off a hidden checklist.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terminal Banner: Chronic Fatigue - pushed to bottom */}
                <div className="mt-auto pt-6">
                  <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200/50 dark:border-rose-800/30 rounded-xl p-5 animate-pulse-soft transform -rotate-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Frown className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                      <h4 className="font-bold text-rose-700 dark:text-rose-300 uppercase tracking-wide text-sm">
                        Chronic Fatigue
                      </h4>
                    </div>
                    <p className="text-rose-700/80 dark:text-rose-300/80 text-sm leading-relaxed">
                      Your brain never enters standby mode. You're physically present, but mentally you're always auditing—managing the family details. It's exhausting.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mood Tags */}
              <div className="mt-6 text-center">
                <p className="text-muted-foreground/60 font-medium tracking-wide">
                  Heavy • Overwhelmed • Solo
                </p>
              </div>
            </div>

            {/* RIGHT SIDE: THE QUIET ENGINE */}
            <div className="relative">
              {/* Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-primary/15 rounded-xl flex items-center justify-center shadow-sm">
                  <ShieldCheck className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-primary/80 uppercase tracking-wider">After the system</p>
                  <h3 className="text-2xl font-bold text-foreground">The Quiet Engine</h3>
                </div>
              </div>

              {/* Organized "Quiet Engine" Stack */}
              <div className="relative space-y-4 pl-8">
                {/* Visual Spine */}
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gradient-to-b from-primary/40 via-emerald-400/40 to-primary/20 rounded-full" />

                {/* Item 1: Automated Logistics */}
                <div className="relative animate-float" style={{ animationDelay: '0s' }}>
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-sm shadow-primary/30" />
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 shadow-lg shadow-primary/5 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/15 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Zap className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Logistics are Automated</h4>
                        <p className="text-muted-foreground text-sm mt-1">
                          You show up to the kitchen table to eat and connect, not to manage a schedule.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Item 2: Shared Master List */}
                <div className="relative animate-float" style={{ animationDelay: '0.5s' }}>
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-3 h-3 bg-emerald-500 rounded-full shadow-sm shadow-emerald-500/30" />
                  <div className="bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/40 dark:border-emerald-800/30 rounded-xl p-5 shadow-lg shadow-emerald-500/5 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                        <ClipboardList className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">The "Master List" is Shared</h4>
                        <p className="text-muted-foreground text-sm mt-1">
                          Information lives in a system that everyone can access. You are no longer the single point of failure.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Item 3: Absolute Visibility */}
                <div className="relative animate-float" style={{ animationDelay: '1s' }}>
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-sm shadow-primary/30" />
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 shadow-lg shadow-primary/5 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/15 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Eye className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Absolute Visibility</h4>
                        <p className="text-muted-foreground text-sm mt-1">
                          No more "I didn't know that was happening." Everyone sees the same reality at the same time.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Item 4: True Presence */}
                <div className="relative animate-float" style={{ animationDelay: '1.5s' }}>
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-3 h-3 bg-emerald-500 rounded-full shadow-sm shadow-emerald-500/30" />
                  <div className="bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/40 dark:border-emerald-800/30 rounded-xl p-5 shadow-lg shadow-emerald-500/5 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Wind className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">True Mental Presence</h4>
                        <p className="text-muted-foreground text-sm mt-1">
                          Calm comes from knowing that if something changes, the system handles the notification. You can just breathe.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terminal Banner: The Reward - BOLD PAYOFF */}
                <div className="relative">
                  <div className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-4 h-4 bg-gradient-to-br from-primary to-emerald-500 rounded-full shadow-lg shadow-primary/30" />
                  <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-emerald-50 dark:from-primary/20 dark:via-primary/10 dark:to-emerald-950/30 border-2 border-primary/30 dark:border-primary/40 rounded-xl p-5 shadow-2xl shadow-primary/15">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-primary/15 rounded-lg flex items-center justify-center ring-2 ring-primary/20">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="font-bold text-primary dark:text-primary uppercase tracking-wide text-sm">
                        A Family Operating System
                      </h4>
                    </div>
                    <p className="text-foreground/80 dark:text-foreground/70 text-sm leading-relaxed">
                      Life is finally lived, not just managed. Clarity replaces the chaos, freeing you to stop tracking and start being a family again.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mood Tags */}
              <div className="mt-6 text-center">
                <p className="text-primary font-semibold tracking-wide">
                  Clear • Present • Balanced
                </p>
              </div>
            </div>
          </div>

          {/* Reinforcing Copy */}
          <div className="mt-20 text-center max-w-3xl mx-auto">
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Systematize the routine to save the relationship.
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Chaos isn't a personality trait; it's a lack of infrastructure. When you move family data out of your head and into a shared system, you stop managing each other and start enjoying each other again.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FamilySystemComparison;
